// Правила и подписи для «Аналитики продаж» и «Рейтинга ОП».
//
// Своих формул по деньгам здесь нет ни одной, и это главное про этот файл. Все суммы,
// конверсии, маржа и места считает ядро (`analytics_api.py`) одним SQL по всей выборке.
// Здесь только то, что нельзя посчитать на сервере, не зная про экран:
//   • названия периодов для выпадающего списка;
//   • форматирование чисел и приростов (включая знак и «не с чем сравнивать»);
//   • подпись бизнес-юнита по справочнику полей;
//   • группировка строк рейтинга по отделам Bitrix.
//
// ПРО ОТДЕЛЫ В РЕЙТИНГЕ — почему группировка здесь, а не в ядре
//
// Человек может состоять в НЕСКОЛЬКИХ отделах (на живых данных таких 19), и «главного»
// отдела в Bitrix нет вовсе. Значит сумма по отделам не обязана совпадать с общим итогом:
// один и тот же человек попадёт в оба своих отдела. Считать это в ядре означало бы отдать
// наружу итог, который не сходится, и объяснить его было бы нечем. Поэтому разбивка
// собирается здесь, из тех же строк, что уже на экране, а сколько людей задвоилось —
// возвращается рядом числом и пишется на экране словами.
//
// Оргструктура берётся из `lib/dashboard.ts` (`makeOrg`) — тем же кодом, что «Кабинет
// руководителя». Второй разбор дерева отделов породил бы вторую версию правды.

import type { RatingRow, UnitRow } from '../api/analytics'
import type { FieldsApi } from '../api/hooks'
import type { OrgApi, RefDepartment } from './dashboard'

/** Бизнес-юнит сделки. В Bitrix это перечисление, в ядре лежит идентификатор значения. */
export const UNIT_FIELD = 'UF_CRM_1570972880'

/** Воронка, по которой портал 55 считает рейтинг. Совпадение цифр держится на ней. */
export const RATING_CATEGORY = 4

/**
 * Периоды в том же наборе, что в портале 55 (месяц / квартал / год / прошлый год), плюс
 * два наших. Названия здесь, а не в ядре: ядро отдаёт свою подпись периода («июль 2026»),
 * а это подписи КНОПОК, они не про данные.
 *
 * `slow` — период, который читает сырые записи Bitrix и стоит секунды. Экран обязан
 * предупредить до нажатия, а не показать «загрузка» на три секунды без объяснений.
 */
export const PERIODS: Array<{ id: string; label: string; slow?: boolean }> = [
  { id: 'month', label: 'Этот месяц' },
  { id: 'prev_month', label: 'Прошлый месяц' },
  { id: 'quarter', label: 'Квартал' },
  { id: 'year', label: 'Год' },
  { id: 'prev_year', label: 'Прошлый год', slow: true },
  { id: 'all', label: 'Всё время', slow: true },
]

// ─────────────────────────── числа ───────────────────────────

/** Целое с разделителями разрядов. `null`/`undefined` → «—», а не «0». */
export function fmtNum(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  return v.toLocaleString('ru-RU', { maximumFractionDigits: 0 })
}

/** Доля в процентах: «41,3 %». `null` — считать не от чего (не «0 %»). */
export function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  return `${v.toLocaleString('ru-RU', { minimumFractionDigits: digits,
    maximumFractionDigits: digits })} %`
}

/**
 * Прирост со знаком: «+24,1 %», «−8,0 %», «0 %».
 *
 * `null` значит «сравнивать не с чем»: в прошлом периоде не было ни рубля, ни сделки.
 * Это НЕ «рост на 100 %» и не «ноль» — база отсутствует, и притворяться, что она есть,
 * значит соврать в самую заметную цифру на экране.
 */
export function fmtDelta(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  const знак = v > 0 ? '+' : (v < 0 ? '−' : '')
  return `${знак}${Math.abs(v).toLocaleString('ru-RU', { minimumFractionDigits: digits,
    maximumFractionDigits: digits })} %`
}

/** Прирост в процентных пунктах — для конверсии: её рост измеряют в п.п., а не в процентах. */
export function fmtDeltaPp(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  const знак = v > 0 ? '+' : (v < 0 ? '−' : '')
  return `${знак}${Math.abs(v).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} п.п.`
}

/**
 * Класс значка для прироста: рост — «won», падение — «lose», иначе нейтральный.
 * Пользуемся ТОЛЬКО существующими классами дизайн-системы (`.badge.won/.lose/.work`),
 * своих цветов не вводим.
 */
export function deltaKind(v: number | null | undefined): 'won' | 'lose' | 'work' {
  if (v === null || v === undefined || !Number.isFinite(v) || v === 0) return 'work'
  return v > 0 ? 'won' : 'lose'
}

/** Доля строки от максимума в списке, 0..100 — ширина полоски в таблице-воронке. */
export function shareOfMax(v: number, max: number): number {
  if (!max || !Number.isFinite(v) || v <= 0) return 0
  return Math.max(1, Math.min(100, Math.round((v / max) * 100)))
}

// ─────────────────────────── месяц ───────────────────────────

const МЕСЯЦЫ = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
                'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь']

/** «2026-07» → «июль 2026». Не разобралось — отдаём как пришло, а не «—». */
export function fmtMonth(m: string): string {
  const [г, мес] = String(m).split('-')
  const i = Number(мес) - 1
  return (i >= 0 && i < 12 && г) ? `${МЕСЯЦЫ[i]} ${г}` : String(m)
}

// ─────────────────────────── бизнес-юнит ───────────────────────────

/**
 * Подпись бизнес-юнита по справочнику полей.
 *
 * Отдельная функция ради одной строчки нужна из-за истории этого поля. В exec-панели юнит
 * хранился ТЕКСТОМ и разъезжался в написаниях: «Автоматика РнД» против «РнД Автоматика»,
 * «Архангельск (Арх.)» против «Архангельск (СиР)», плюс мусорное «Архангельск (не
 * использовать)». Группировка строгим сравнением по названию стоила exec-порталу четырёх
 * руководителей, видевших нули.
 *
 * В ядре этой проблемы нет: поле — перечисление Bitrix, и в записи лежит идентификатор
 * значения («787»), а не строка. Группировать по нему точно, сводить написания не нужно.
 * Название берём из `enum_map` справочника `/fields`; нет справочника — показываем код,
 * это честнее пустого места.
 *
 * Пустой юнит подписываем словами. На 28.07.2026 он пуст у 43 767 сделок из 74 655.
 * Он не «прочее»: это почти половина выручки и самая крупная строка разреза, прятать
 * её за «—» нельзя.
 */
export function unitLabel(unit: string, fields: FieldsApi): string {
  if (!unit) return 'Не заполнен'
  const карта = fields.meta(UNIT_FIELD)?.enum_map
  return карта?.[unit] || `Значение #${unit}`
}

/** Сумма и доля пустого юнита — чтобы сказать о ней явно, а не оставить в таблице. */
export function emptyUnitShare(rows: UnitRow[]): { sum: number; count: number; pct: number | null } {
  const всего = rows.reduce((s, r) => s + r.sum, 0)
  const пусто = rows.find((r) => r.empty)
  if (!пусто) return { sum: 0, count: 0, pct: всего ? 0 : null }
  return { sum: пусто.sum, count: пусто.count, pct: всего ? (пусто.sum / всего) * 100 : null }
}

// ─────────────────────────── отделы в рейтинге ───────────────────────────

export interface DeptGroup {
  dept: RefDepartment | null
  /** null-отдел — люди, которых в оргструктуре нет вовсе (обычно уволенные). */
  rows: RatingRow[]
  deals: number
  revenue: number
  profit: number
  /** Доли, которые бизнес-процесс записал отделу продаж и сервису. Складываются так же. */
  profit_op: number
  profit_service: number
}

export interface DeptBreakdown {
  groups: DeptGroup[]
  /** Сколько человек попало больше чем в один отдел — из-за них сумма по отделам больше итога. */
  doubled: number
  /** Сколько человек не нашлось в оргструктуре: их сделки есть, а отдела у них нет. */
  orphans: number
}

/**
 * Разбивка рейтинга по отделам Bitrix.
 *
 * Портал 55 делал то же самое по списку фамилий, зашитому в код («ОП1», «ОП2», «Механика»,
 * «Ростов»…). Здесь — настоящая оргструктура, поэтому новички и переводы подхватываются
 * сами. Плата за честность: человек в двух отделах попадёт в оба, и сумма по отделам будет
 * БОЛЬШЕ общего итога. Мы это не скрываем и не «исправляем» дележом пополам — считаем,
 * сколько таких людей, и пишем на экране.
 *
 * `orphans` — отдельная группа, а не «прочее»: 8 999 сделок висят на уволенных (28.07.2026),
 * и в составе отделов (`/refs` отдаёт только действующих) их нет. Выбросить их значило бы
 * недосчитать историю отдела.
 */
export function byDepartment(rows: RatingRow[], org: OrgApi): DeptBreakdown {
  const по_отделам = new Map<number, DeptGroup>()
  const сироты: RatingRow[] = []
  let doubled = 0

  for (const r of rows) {
    const отделы = r.user_id === null ? [] : org.memberOf(String(r.user_id))
    if (отделы.length === 0) {
      сироты.push(r)
      continue
    }
    if (отделы.length > 1) doubled += 1
    for (const d of отделы) {
      let g = по_отделам.get(d.id)
      if (!g) {
        g = { dept: d, rows: [], deals: 0, revenue: 0, profit: 0,
              profit_op: 0, profit_service: 0 }
        по_отделам.set(d.id, g)
      }
      g.rows.push(r)
      g.deals += r.deals
      g.revenue += r.revenue
      g.profit += r.profit
      g.profit_op += r.profit_op
      g.profit_service += r.profit_service
    }
  }

  const groups = [...по_отделам.values()].sort((a, b) => b.profit - a.profit)
  if (сироты.length) {
    groups.push({
      dept: null,
      rows: сироты,
      deals: сироты.reduce((s, r) => s + r.deals, 0),
      revenue: сироты.reduce((s, r) => s + r.revenue, 0),
      profit: сироты.reduce((s, r) => s + r.profit, 0),
      profit_op: сироты.reduce((s, r) => s + r.profit_op, 0),
      profit_service: сироты.reduce((s, r) => s + r.profit_service, 0),
    })
  }
  return { groups, doubled, orphans: сироты.length }
}

/**
 * Отдел, с которого разумно открыть рейтинг: «Коммерческая служба», если она есть.
 *
 * Портал 55 считает рейтинг по коммерческой службе, и открывать наш экран на всей компании
 * значило бы показать другой список людей при тех же цифрах. Ищем по названию, а не по
 * зашитому номеру: номер отдела — настройка Bitrix, а переименование «Коммерческой службы»
 * заметят и поправят здесь, тогда как молча сломавшийся номер 5 никто не увидит.
 * Не нашли — открываем по всей компании, но пишем об этом.
 */
export function commercialDept(departments: RefDepartment[] | undefined): RefDepartment | null {
  // Плоский список из /refs, а не обход дерева от корня: корень пришлось бы задать номером,
  // а номер отдела — настройка Bitrix, которая однажды поменяется молча.
  return (departments ?? []).find((d) => !d.deleted && /коммерческ/i.test(d.name || '')) ?? null
}

/** Значок лиги. Лиги — соглашение портала 55, а не расчёт; классы только из дизайн-системы. */
export function leagueKind(league: string): 'won' | 'work' | '' {
  if (league === 'Высшая') return 'won'
  if (league === 'Первая') return 'work'
  return ''
}
